import { Button, Form, Skeleton } from "antd";
import { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import type { Dispatch } from "@reduxjs/toolkit";
import type { Location } from "../generated-axios-client/index.ts";
import type { IntlShape } from "@formatjs/intl";
import { actions as routerActions } from "redux-router5";
import ConnectedAppLayout from "../AppLayout.tsx";
import ModalFooter from "../../common/components/ModalFooter.tsx";
import { PageTitleContainer } from "../../common/components/PageTitle.tsx";
import { locationActions } from "../../features/location/locationSlice.ts";
import { routeNames } from "../common/routes.ts";
import { LocationFields } from "./LocationFields.tsx";
import type { RootState } from "../store.ts";

type NewLocationPageProps = {
	dispatch: Dispatch;
	intl: IntlShape;
	initialValue?: Location | null;
};

export function NewLocationPage({
	dispatch,
	intl,
	initialValue,
}: NewLocationPageProps) {
	const [form] = Form.useForm();

	const save = () => {
		form
			.validateFields()
			.then((values) => {
				const payload = initialValue?.id
					? { ...values, id: initialValue.id }
					: values;
				dispatch(locationActions.addOrUpdateLocation(payload));
			})
			.catch((errorInfo) => {
				console.error("Validation failed:", errorInfo);
			});
	};

	const initialValues = useMemo(
		() => (initialValue ? { ...initialValue } : undefined),
		[initialValue],
	);

	useEffect(() => {
		if (initialValues) {
			form.setFieldsValue(initialValues);
		} else {
			form.resetFields();
		}
	}, [initialValues, form]);

	const isEdit = !!initialValue?.id;

	if (isEdit && !initialValue) {
		return <Skeleton active />;
	}

	return (
		<ConnectedAppLayout>
			<PageTitleContainer
				title={intl.formatMessage({
					id: isEdit ? "location_edit_title" : "location_create_title",
				})}
				subtitle={
					isEdit
						? undefined
						: intl.formatMessage({ id: "location_create_subtitle" })
				}
			>
				<Form form={form} layout="vertical" initialValues={initialValues}>
					<LocationFields intl={intl} />
				</Form>
				<ModalFooter>
					<Button
						onClick={() =>
							dispatch(
								routerActions.navigateTo(
									isEdit ? routeNames.LOCATION_VIEW : routeNames.LOCATION_LIST,
									isEdit ? { id: initialValue!.id } : undefined,
								),
							)
						}
					>
						{intl.formatMessage({ id: "ticket_button_title_cancel" })}
					</Button>
					<Button type="primary" onClick={save}>
						{intl.formatMessage({ id: "ticket_button_title_create" })}
					</Button>
				</ModalFooter>
			</PageTitleContainer>
		</ConnectedAppLayout>
	);
}

export const ConnectedNewLocationPage = connect((state: RootState) => ({
	intl: state.intl.datetimeIntl,
	initialValue: state.location.location,
}))(NewLocationPage);

export default ConnectedNewLocationPage;
